import { useContext, useState } from 'react';
import {
  BasicCollapsible,
  Button,
  FlexBox,
  FormField,
  FormInputGroup,
  FormLabel,
  Icon,
  ModalDialog,
  ModalDialogBody,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogNav,
  ModalDialogTitle,
  StandardForm,
  TextInput,
  Typography,
} from '@vp/swan';
import { LocalizationContext } from '@vp/om-translations';
import { useSiteData } from '../../hooks/useSiteData';
import { useDebugModeContext } from '../contexts/DebugModeContext';
import { DebugExperimentsPanel } from './DebugExperimentsPanel/DebugExperimentsPanel';
import { DebugFeaturePanel } from './DebugFeaturePanel/DebugFeaturePanel';
import { DebugModPanel } from './DebugModPanel/DebugModPanel';
import { useShopperId } from '../../hooks/useShopperId';

export const DebugTools = () => {
  const shopperId = useShopperId();
  const { vistacartTenant: tenant } = useSiteData();
  const { debugModeEnabled, setDebugModeEnabled } = useDebugModeContext();
  const { targetLocale, setLocale } = useContext(LocalizationContext);

  const [isOpen, setIsOpen] = useState(false);

  if (!debugModeEnabled) {
    return null;
  }

  return (
    <div className="debug-tools">
      <FlexBox
        backgroundColor="accent"
        p={7}
        mt={5}
        mb={7}
        flexDirection="row"
        justifyContent="space-between"
      >
        <FlexBox
          flexDirection="column"
          justifyContent="space-between"
          alignItems="stretch"
        >
          {/* Shopper ID */}
          <Typography>
            <b>Shopper ID: </b>
            {shopperId}
          </Typography>
          {/* Tenant */}
          <Typography>
            <b>Tenant: </b>
            {tenant}
          </Typography>
        </FlexBox>
        <FlexBox
          flexDirection="column"
          justifyContent="space-between"
          style={{ minWidth: '10%', width: '240px' }}
          flexWrap="nowrap"
        >
          <Button onClick={() => setIsOpen(true)} iconPosition="right">
            Debug Tools
            <Icon alt="" iconType="chevronLeft" />
          </Button>
        </FlexBox>
      </FlexBox>
      <ModalDialog
        variant="panel-right"
        isOpen={isOpen}
        bodyWidth="standard"
        onRequestDismiss={() => {
          setIsOpen(false);
        }}
        onlyRenderWhenOpen
      >
        <ModalDialogContent style={{ maxWidth: '60%', minWidth: '30%' }}>
          <ModalDialogNav>
            <ModalDialogCloseButton accessibleText="Close" />
          </ModalDialogNav>
          <ModalDialogHeader>
            <ModalDialogTitle>Debug Tools</ModalDialogTitle>
          </ModalDialogHeader>
          <ModalDialogBody>
            <FlexBox flexDirection="column" justifyContent="start" pb={5}>
              {/* Shopper ID */}
              <Typography>
                <b>Shopper ID: </b>
                {shopperId}
              </Typography>
              {/* Tenant */}
              <Typography>
                <b>Tenant: </b>
                {tenant}
              </Typography>
            </FlexBox>

            {/* Site data & localization */}
            <BasicCollapsible heading="Site data & localization" mb={5}>
              {/* Tenant */}
              <FlexBox alignItems="center" my={3}>
                <Typography fontWeight="bold" fontSize="small" mr={3}>
                  Tenant:
                </Typography>
                <Typography ml={5}>{tenant}</Typography>
              </FlexBox>

              {/* Locale */}
              <FlexBox alignItems="center" my={3}>
                <StandardForm variant="horizontal">
                  <FormField>
                    <FormLabel>Locale:</FormLabel>
                    <FormInputGroup>
                      <TextInput
                        value={targetLocale}
                        onChange={({ target: { value } }) => setLocale(value)}
                      />
                    </FormInputGroup>
                  </FormField>
                </StandardForm>
              </FlexBox>
            </BasicCollapsible>

            {/* Experiments */}
            <BasicCollapsible
              heading="Experiment settings"
              mb={5}
              collapsibleId="Experiment settings"
            >
              <DebugExperimentsPanel />
            </BasicCollapsible>

            {/* Feature toggles */}
            <BasicCollapsible
              heading="Feature settings"
              mb={5}
              collapsibleId="Feature settings"
            >
              <DebugFeaturePanel />
            </BasicCollapsible>
            <BasicCollapsible heading="Page Mods" collapsibleId="Page Mods">
              <DebugModPanel />
            </BasicCollapsible>
          </ModalDialogBody>
          <ModalDialogFooter pinned>
            {/* Buttons */}
            <Button
              skin="primary"
              width="full-width"
              mb={3}
              onClick={() => {
                setIsOpen(false);
              }}
            >
              Close
            </Button>
            <Button
              skin="secondary"
              width="full-width"
              onClick={() => {
                setDebugModeEnabled(!debugModeEnabled);
                // corejs3 has a polyfill for URLSearchParams so it is safe to use
                const qs = new URLSearchParams(window.location.search).delete(
                  'debug'
                );
                window.location.replace(
                  window.location.pathname +
                    (typeof qs === 'undefined' ? '' : qs)
                );
              }}
            >
              Exit debug mode
            </Button>
          </ModalDialogFooter>
        </ModalDialogContent>
      </ModalDialog>
    </div>
  );
};
